import React, { useRef } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  IconButton,
  Typography,
  Stack,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';

/**
 * 개선된 파일 업로드 폼 (V2)
 * 
 * 개선사항:
 * - 드래그앤드롭 지원
 * - 파일 미리보기
 * - 다중 파일 업로드
 * - 파일 타입별 아이콘
 */
const FileUploadFormV2 = ({ formik }) => {
  const fileInputRef = useRef(null);

  // 파일 아이콘 선택
  const getFileIcon = (fileName) => {
    if (!fileName) return <FileIcon />;
    
    const ext = fileName.split('.').pop().toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
      return <ImageIcon color="primary" />;
    }
    if (ext === 'pdf') {
      return <PdfIcon color="error" />;
    }
    if (['doc', 'docx'].includes(ext)) {
      return <DescriptionIcon color="info" />;
    }
    return <FileIcon color="action" />;
  };

  // 파일 크기 포맷
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // 파일 추가
  const handleAddFile = () => {
    const currentFiles = formik.values.files || [];
    formik.setFieldValue('files', [
      ...currentFiles,
      { fileDesc: '', file: null },
    ]);
  };

  // 파일 삭제
  const handleRemoveFile = (index) => {
    const currentFiles = [...(formik.values.files || [])];
    currentFiles.splice(index, 1);
    
    // 최소 1개는 유지
    if (currentFiles.length === 0) {
      currentFiles.push({ fileDesc: '', file: null });
    }
    
    formik.setFieldValue('files', currentFiles);
  };

  // 파일 선택
  const handleFileChange = (index, event) => {
    const file = event.target.files[0];
    if (file) {
      const currentFiles = [...(formik.values.files || [])];
      currentFiles[index] = {
        ...currentFiles[index],
        file: file,
      };
      formik.setFieldValue('files', currentFiles);
    }
  };

  // 파일 설명 변경
  const handleDescChange = (index, value) => {
    const currentFiles = [...(formik.values.files || [])];
    currentFiles[index] = {
      ...currentFiles[index],
      fileDesc: value,
    };
    formik.setFieldValue('files', currentFiles);
  };

  // 드래그앤드롭 핸들러
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (index) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer.files[0];
    if (file) {
      const currentFiles = [...(formik.values.files || [])];
      currentFiles[index] = {
        ...currentFiles[index],
        file: file,
      };
      formik.setFieldValue('files', currentFiles);
    }
  };

  const files = formik.values.files || [{ fileDesc: '', file: null }];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          첨부파일 ({files.filter(f => f.file).length}개 선택됨)
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddFile}
          variant="outlined"
        >
          파일 추가
        </Button>
      </Stack>

      <List disablePadding>
        {files.map((fileItem, index) => (
          <React.Fragment key={index}>
            <ListItem 
              sx={{ 
                px: 0, 
                py: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2,
              }}
            >
              {/* 파일 선택 영역 */}
              <Paper
                variant="outlined"
                onDragOver={handleDragOver}
                onDrop={handleDrop(index)}
                sx={{
                  p: 2,
                  minWidth: { xs: '100%', sm: 250 },
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: fileItem.file ? 'success.50' : 'grey.50',
                  borderStyle: 'dashed',
                  borderColor: fileItem.file ? 'success.main' : 'grey.300',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'primary.50',
                  },
                }}
                onClick={() => {
                  const input = document.getElementById(`file-input-${index}`);
                  if (input) input.click();
                }}
              >
                <input
                  id={`file-input-${index}`}
                  type="file"
                  hidden
                  onChange={(e) => handleFileChange(index, e)}
                />
                
                {fileItem.file ? (
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                    {getFileIcon(fileItem.file.name)}
                    <Box sx={{ textAlign: 'left', overflow: 'hidden' }}>
                      <Typography 
                        variant="body2" 
                        fontWeight={500}
                        noWrap
                        sx={{ maxWidth: 180 }}
                      >
                        {fileItem.file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(fileItem.file.size)}
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  <Stack alignItems="center" spacing={0.5}>
                    <CloudUploadIcon color="action" />
                    <Typography variant="caption" color="text.secondary">
                      클릭하거나 파일을 드래그하세요
                    </Typography>
                  </Stack>
                )}
              </Paper>

              {/* 파일 설명 */}
              <TextField
                size="small"
                label="파일 설명"
                value={fileItem.fileDesc || ''}
                onChange={(e) => handleDescChange(index, e.target.value)}
                placeholder="파일에 대한 설명을 입력하세요"
                sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 } }}
              />

              {/* 삭제 버튼 */}
              <IconButton
                color="error"
                onClick={() => handleRemoveFile(index)}
                disabled={files.length === 1 && !fileItem.file}
                sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
              >
                <DeleteIcon />
              </IconButton>
            </ListItem>
            
            {index < files.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>

      {/* 안내 메시지 */}
      <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.50', borderRadius: 1 }}>
        <Typography variant="caption" color="info.main">
          💡 지원 파일 형식: PDF, Word, Excel, 이미지 (최대 10MB)
        </Typography>
      </Box>
    </Box>
  );
};

export default FileUploadFormV2;
